/**
 * MobileInit - Mobile App Initialization Component
 * 
 * Handles mobile-specific initialization including Capacitor services,
 * background audio setup, and mobile platform detection.
 */

import { useEffect, useState } from 'react';
import { useCapacitor } from '../hooks/useCapacitor';

const MobileInit = ({ children }) => {
  const [initStatus, setInitStatus] = useState('initializing');
  const { initialized, isNative, platform } = useCapacitor();

  useEffect(() => {
    const initializeMobile = async () => {
      try {
        console.log(`🚀 Aprendos Mobile Init - Platform: ${platform}, Native: ${isNative}`);
        
        if (isNative) {
          console.log('📱 Native mobile platform detected');
          console.log('🎵 Background audio capabilities enabled');
          console.log('🔔 Push notifications available');
          console.log('💾 Offline storage ready');
          
          // Set mobile-specific configurations
          document.body.classList.add('mobile-native');
          
          // Prevent zooming on inputs (mobile optimization)
          if (platform === 'ios') {
            const viewport = document.querySelector('meta[name="viewport"]');
            if (viewport) {
              viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
            }
          }
          
          // Add mobile-specific CSS classes
          document.documentElement.classList.add(`platform-${platform}`);
          
        } else {
          console.log('🌐 Web platform - PWA features available');
          document.body.classList.add('mobile-web');
        }

        setInitStatus('ready');
      } catch (error) {
        console.error('❌ Mobile initialization failed:', error);
        setInitStatus('error');
      }
    };

    if (initialized) {
      initializeMobile();
    }
  }, [initialized, isNative, platform]);

  // Show loading screen during initialization
  if (initStatus === 'initializing') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-violet-500 to-yellow-400">
        <div className="text-center text-white">
          <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold mb-2">Initializing Aprendos</h2>
          <p className="text-white/80">Setting up your language learning experience...</p>
          {isNative && (
            <div className="mt-4 text-sm text-white/60">
              <p>📱 Platform: {platform}</p>
              <p>🎵 Background audio ready</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show error state if initialization fails
  if (initStatus === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-2xl">⚠️</span>
          </div>
          <h2 className="text-xl font-semibold text-red-800 mb-2">Initialization Failed</h2>
          <p className="text-red-600 mb-4">We encountered an issue setting up the mobile experience.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Mobile app is ready - render children
  return (
    <>
      {children}
      {/* Mobile-specific UI elements */}
      {isNative && <MobileStatusBar />}
    </>
  );
};

// Mobile status bar component for native apps
const MobileStatusBar = () => {
  return (
    <div className="mobile-status-indicator fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 to-yellow-400 z-50 opacity-20"></div>
  );
};

export default MobileInit;
